import { createServerFn } from '@tanstack/react-start';

/*
 * Live FX quote proxy.
 *
 * The browser must never call `api.nsave.com` directly: the upstream only
 * allows `nsave.com` origins, so a direct client fetch is blocked by CORS.
 * Instead this `createServerFn` runs on our own server (Nitro), fetches the
 * upstream server-to-server (CORS does not apply), and returns the normalized
 * quote to the client — mirroring the arrow-dev proxy pattern
 * (`NEXT_PUBLIC_API_URL=.../api` + rewrite -> browser hits its own origin).
 *
 * Upstream (public, no auth — a `@Public()` route):
 *   GET `${FX_API_BASE_URL}/transactions/fx/rate/public`
 *       ?sourceCurrency=USD&targetCurrency=EGP&amount=1000&pricingTier=PRO
 *   -> { exchangeRate: string, fee: string|number, amount: string }
 *
 * Base URL is configurable via `FX_API_BASE_URL` (default `https://api.nsave.com`),
 * so dev can point at `https://api.dev.nsave.com`.
 */

// Server-only runtime env (Nitro). Declared locally so we don't need @types/node.
declare const process: { env: Record<string, string | undefined> };

export type Quote = { rate: number; fee: number; amount: number };

/** Send currency is USD only for now. */
export const SOURCE_CURRENCY = 'USD';

/** Supported payout currencies (guards the proxy against arbitrary passthrough). */
export const RECEIVE_CURRENCIES = ['EGP', 'BDT', 'PKR', 'NGN', 'MAD', 'TRY'] as const;
export type ReceiveCurrency = (typeof RECEIVE_CURRENCIES)[number];

/**
 * Static PRO-tier per-unit rates (1 USD -> N target). Client-safe fallback used
 * by the calculator and the rates strip whenever the live upstream is
 * unreachable, so neither ever shows a broken/empty state.
 */
export const FALLBACK_RATES: Record<ReceiveCurrency, number> = {
  EGP: 49.47,
  BDT: 119.6,
  PKR: 278.4,
  NGN: 1533.2,
  MAD: 9.95,
  TRY: 34.2,
};

export const PRICING_TIERS = ['PRO', 'FREE'] as const;
export type PricingTier = (typeof PRICING_TIERS)[number];

/** Coerce an arbitrary string into a supported pricing tier (default PRO). */
function normalizeTier(tier?: string): PricingTier {
  const t = String(tier ?? 'PRO').toUpperCase();
  return (PRICING_TIERS as readonly string[]).includes(t) ? (t as PricingTier) : 'PRO';
}

const DEFAULT_BASE_URL = 'https://api.nsave.com';
/** Clamp the amount to a sane server-side range regardless of client input. */
const MAX_AMOUNT = 1_000_000;

export type QuoteInput = {
  targetCurrency: string;
  amount: number;
  pricingTier?: string;
};

function validateInput(input: QuoteInput): {
  targetCurrency: ReceiveCurrency;
  amount: number;
  pricingTier: PricingTier;
} {
  const targetCurrency = String(input?.targetCurrency ?? '').toUpperCase();
  if (!(RECEIVE_CURRENCIES as readonly string[]).includes(targetCurrency)) {
    throw new Error(`Unsupported target currency: ${input?.targetCurrency}`);
  }

  const rawAmount = Number(input?.amount);
  if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
    throw new Error(`Invalid amount: ${input?.amount}`);
  }
  const amount = Math.min(rawAmount, MAX_AMOUNT);

  return {
    targetCurrency: targetCurrency as ReceiveCurrency,
    amount,
    pricingTier: normalizeTier(input?.pricingTier),
  };
}

/**
 * Server-to-server fetch of a single live quote from the nsave backend.
 * Throws on network error, non-200, or a malformed body. Shared by both
 * `getQuote` (single) and `getRates` (batch).
 */
async function fetchQuoteUpstream(
  targetCurrency: ReceiveCurrency,
  amount: number,
  pricingTier: PricingTier,
): Promise<Quote> {
  const baseUrl = process.env.FX_API_BASE_URL || DEFAULT_BASE_URL;
  const url = new URL('/transactions/fx/rate/public', baseUrl);
  url.searchParams.set('sourceCurrency', SOURCE_CURRENCY);
  url.searchParams.set('targetCurrency', targetCurrency);
  url.searchParams.set('amount', String(amount));
  url.searchParams.set('pricingTier', pricingTier);

  let response: Response;
  try {
    response = await fetch(url, {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(8000),
    });
  } catch (cause) {
    throw new Error(`FX upstream unreachable: ${String(cause)}`, { cause });
  }

  if (!response.ok) {
    throw new Error(`FX upstream error: ${response.status} ${response.statusText}`);
  }

  const body = (await response.json()) as {
    exchangeRate?: string | number;
    fee?: string | number;
    amount?: string | number;
  };

  const rate = Number(body.exchangeRate);
  const fee = Number(body.fee);
  const receiveAmount = Number(body.amount);
  if (!Number.isFinite(rate) || !Number.isFinite(fee) || !Number.isFinite(receiveAmount)) {
    throw new Error('FX upstream returned a malformed quote');
  }

  return { rate, fee, amount: receiveAmount };
}

/**
 * Fetch a live FX quote from the nsave backend. Runs server-side only.
 * Throws on invalid input or upstream failure; callers fall back to their own
 * static estimate so the UI never shows a broken state.
 */
export const getQuote = createServerFn({ method: 'GET' })
  .validator((input: QuoteInput) => input)
  .handler(async ({ data }): Promise<Quote> => {
    const { targetCurrency, amount, pricingTier } = validateInput(data);
    return fetchQuoteUpstream(targetCurrency, amount, pricingTier);
  });

export type RateEntry = { code: ReceiveCurrency; rate: number };

export type RatesInput = { pricingTier?: string };

/**
 * Batch-fetch the live per-unit rate (1 USD -> N target) for every supported
 * payout currency in parallel, in a single client round-trip. Runs server-side.
 * Honours the pricing tier so the strip reflects the calculator's selected plan.
 * Pairs whose upstream call fails are omitted; the client falls back to
 * `FALLBACK_RATES` for anything missing so the strip always renders all pairs.
 */
export const getRates = createServerFn({ method: 'GET' })
  .validator((input?: RatesInput) => input ?? {})
  .handler(async ({ data }): Promise<RateEntry[]> => {
    const pricingTier = normalizeTier(data?.pricingTier);
    const settled = await Promise.allSettled(
      RECEIVE_CURRENCIES.map(async (code): Promise<RateEntry> => {
        const quote = await fetchQuoteUpstream(code, 1000, pricingTier);
        return { code, rate: quote.rate };
      }),
    );

    return settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
  });
